/* eslint-disable react/no-danger */
import { Flex } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Header, Text, Tooltip, TooltipPlacement } from '../../ui-components';
import { Accordion, AccordionItem } from '../../ui-components/accordion';
import { useContentAsset } from '../../utils/api';
import { ContentAssetID } from '../../utils/api/ContentAssetID';
import { sanitizeHtml } from '../content-asset/sanitize-html';
import { TextReadMore } from '../text-read-more';
import styles from './ProductInformation.module.scss';
import type { Product } from '../../bff-api/products/model';

interface Props {
    product: Product;
}

export const ProductInformation = ({
    product: {
        id,
        longDescription = '',
        c_features,
        c_specifications,
        c_whatsInBox,
        c_careusage,
        c_hazmatMessage,
        c_legalMessage,
    },
}: Props): React.ReactElement<Props> => {
    const intl = useIntl();
    const shippingReturns = useContentAsset(ContentAssetID.PRODUCT_SHIPPING_RETURNS);

    return (
        <Flex
            data-testid="product-information"
            direction="column"
            className={styles.product_information}
            paddingX={{ base: 4, sm: 8, md: 0 }}
        >
            <Header
                id="product-information-title"
                as="h2"
                style="bold"
                color="slt-pepper.1000"
                variant="header4"
            >
                <FormattedMessage
                    id="product.information.why.you.will.love.it"
                    defaultMessage="Why you'll love it"
                />
            </Header>

            <TextReadMore id="product-information-title-description" margin="8px 0 0 0">
                {longDescription}
            </TextReadMore>

            <Text
                id="product-information-title-sku"
                margin="16px 0 24px 0"
                style="bold"
                color="slt-pepper.1000"
            >
                <FormattedMessage
                    id="product.information.sku"
                    defaultMessage="SKU {id}"
                    values={{ id }}
                />
            </Text>

            <Accordion id="product-information-accordion">
                {c_features ? (
                    <AccordionItem
                        id="product-information-accordion-item-product-features"
                        data-testid="product-information-accordion-item-product-features"
                        title={intl.formatMessage({
                            id: 'product.information.product.features',
                            defaultMessage: 'Product Features',
                        })}
                        titleTag="h2"
                    >
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(c_features) }} />

                        {c_hazmatMessage ? (
                            <ul>
                                <li
                                    className="mt-3"
                                    data-testid="product-information.hazmat-certified.item"
                                >
                                    <FormattedMessage
                                        id="product.information.hazmat.certified"
                                        defaultMessage="Hazmat Certified"
                                    />
                                    <Tooltip
                                        label={
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: sanitizeHtml(c_hazmatMessage),
                                                }}
                                            />
                                        }
                                        placement={TooltipPlacement.top}
                                    >
                                        <div className="ml-7 cursor-pointer underline">
                                            <FormattedMessage
                                                id="product.information.hazmat.certified.see.details"
                                                defaultMessage="See Details"
                                            />
                                        </div>
                                    </Tooltip>
                                </li>
                            </ul>
                        ) : null}

                        {c_legalMessage ? (
                            <ul>
                                <li
                                    className={styles.legal_message}
                                    data-testid="product-information.legal-message.item"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(
                                            c_legalMessage?.replace(
                                                /<a /g,
                                                '<a target="_blank" rel="noopener" ',
                                            ),
                                        ),
                                    }}
                                />
                            </ul>
                        ) : null}
                    </AccordionItem>
                ) : null}

                {c_specifications ? (
                    <AccordionItem
                        id="product-information-accordion-item-specifications"
                        title={intl.formatMessage({
                            id: 'product.information.specifications',
                            defaultMessage: 'Specifications',
                        })}
                        titleTag="h2"
                    >
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(c_specifications) }} />
                    </AccordionItem>
                ) : null}

                {c_whatsInBox ? (
                    <AccordionItem
                        id="product-information-accordion-item-whats-in-the-box"
                        title={intl.formatMessage({
                            id: 'what.is.in.the.box',
                            defaultMessage: "What's in the box",
                        })}
                        titleTag="h2"
                    >
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(c_whatsInBox) }} />
                    </AccordionItem>
                ) : null}

                {c_careusage ? (
                    <AccordionItem
                        id="product-information-accordion-item-care-usage"
                        title={intl.formatMessage({
                            id: 'care.&.usage',
                            defaultMessage: 'Care & Usage',
                        })}
                        titleTag="h2"
                    >
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(c_careusage) }} />
                    </AccordionItem>
                ) : null}

                {shippingReturns ? (
                    <AccordionItem
                        id="product-information-accordion-item-shipping-returns"
                        title={intl.formatMessage({
                            id: 'product.information.shipping.returns',
                            defaultMessage: 'Shipping & Returns',
                        })}
                        titleTag="h2"
                    >
                        <div
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(shippingReturns) }}
                            className={styles.shipping_returns}
                        />
                    </AccordionItem>
                ) : null}
            </Accordion>
        </Flex>
    );
};
